/**
 * Tool-action gerund labels for the chat streaming activity timeline.
 */

/**
 * Simple gerund labels for tool actions — one word, natural English.
 * The receipt summary from the backend provides the "result" line.
 */
export const TOOL_GERUNDS: Record<string, string> = {
  // Data
  list_objects:      'Looking',
  get_object:        'Looking',
  read_asset:        'Reading',
  // CRUD
  create_object:     'Creating',
  update_object:     'Updating',
  delete_object:     'Deleting',
  restore_object:    'Restoring',
  // Links
  link_object:       'Linking',
  unlink_object:     'Unlinking',
  // Search
  search:            'Searching',
  semantic_search:   'Searching',
  search_internet:   'Searching',
  places_autocomplete: 'Looking',
  // Memory
  recall_memories:   'Checking',
  save_memory:       'Saving',
  forget_memory:     'Clearing',
  // Modules
  create_module:     'Creating',
  manage_module:     'Managing',
  create_data_module:'Creating',
  // Integrations
  connect_integration: 'Connecting',
  list_integrations: 'Checking',
  query_integration: 'Querying',
  sync_integration:  'Syncing',
  // Misc
  render_visualization: 'Rendering',
  aggregate_content: 'Gathering',
  flight_lookup:     'Looking',
  get_plugin_analytics: 'Checking',
  // Agent actions
  exec:              'Running',
  ui_action:         'Working',
  bash:              'Running',
  search_objects:    'Searching',
  memory:            'Checking memory',
  health_log:        'Logging',
  create_reminder:   'Setting reminder',
  profile_lookup:    'Looking up',
  skills_list:       'Checking skills',
  skill_view:        'Loading skill',
}

/**
 * Get the gerund label for a tool action for the chat activity timeline.
 *
 * The label is intentionally just the action verb (e.g. "Deleting...",
 * "Searching...") — the object type is NOT appended, so the pill stays terse
 * ("Deleting..." rather than "Deleting event...", "Working..." rather than
 * "Working event...").
 *
 * Object CRUD is multiplexed through the single `xavior_api` tool via an `action`
 * param (`create_object`/`update_object`/`delete_object`/…) rather than distinct
 * tool names, so when the tool name itself has no gerund we resolve the `action`
 * to its gerund. That keeps deletes reading "Deleting..." (and creates
 * "Creating...") instead of collapsing to a generic "Working..." (which the
 * activity timeline then suppresses as uninformative).
 *
 * Unknown tool names AND unknown actions fall back to the generic "Working"
 * gerund — the raw tool name / action is NEVER emitted (#4766). `action` is used
 * only as a map lookup key, never interpolated, so a raw identifier (e.g.
 * `team_member`) can't leak into the visible label.
 */
export function getToolLabel(toolName: string, toolParams?: Record<string, unknown>): string {
  const action = typeof toolParams?.action === 'string' ? toolParams.action : ''
  const key = TOOL_GERUNDS[toolName]
    ? toolName
    : (action && TOOL_GERUNDS[action] ? action : toolName)
  return (TOOL_GERUNDS[key] || 'Working') + '...'
}
